import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '@/contexts/LanguageContext';
import { useSubscription } from '@/contexts/SubscriptionContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { LogOut, ChevronDown, ChevronLeft, Lock, BarChart3, PieChartIcon, LineChartIcon, Home } from 'lucide-react';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { SubscriptionBanner } from '@/components/SubscriptionBanner';
import { AnalysisPriceChart } from '@/components/AnalysisPriceChart';
import { AnalysisPriceLineChart } from '@/components/AnalysisPriceLineChart';
import { HistoryOfSuccess } from '@/components/HistoryOfSuccess';

// Backend URL - Keep from new version but add fallback
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'https://platform.tahlil.one';

// DEVELOPMENT MODE - Set to true to bypass all authentication
const DEVELOPMENT_MODE = false;

export default function UserDashboard({ user: propUser }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { t, language } = useLanguage();
  const { subscriptionStatus } = useSubscription();

  // State declarations - combine both versions
  const [user, setUser] = useState(propUser || location.state?.user || null);
  const [isAuthenticated, setIsAuthenticated] = useState(!!(propUser || location.state?.user));
  const [markets, setMarkets] = useState([]);
  const [assets, setAssets] = useState([]);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [analysis, setAnalysis] = useState(null);
  const [expandedMarkets, setExpandedMarkets] = useState({});
  const [accessDenied, setAccessDenied] = useState(false);
  const [isLoading, setIsLoading] = useState({
    markets: false,
    assets: false,
    analysis: false
  });

  // Authentication check - FIXED VERSION
  useEffect(() => {
    if (user) return; // Already have user from props or location state

    const checkAuth = async () => {
      // DEVELOPMENT MODE: Always authenticate with dummy user
      if (DEVELOPMENT_MODE) {
        console.log('🔧 DEVELOPMENT MODE: Using dummy user for authentication');
        const dummyUser = {
          user_id: "dev_user_123",
          email: "fmukhlisov@gmail.com",
          name: "Faisal Mukhlisov",
          picture: "",
          access_level: "admin",
          subscription_type: "Premium",
          subscription_status: "active",
          subscription_start_date: new Date().toISOString(),
          subscription_end_date: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
          created_at: new Date().toISOString()
        };

        setUser(dummyUser);
        setIsAuthenticated(true);

        // Still try real auth in background for debugging
        tryRealAuth();
        return;
      }

      // ORIGINAL AUTH CHECK (for production)
      try {
        console.log('🍪 Current cookies:', document.cookie);
        console.log('🌐 Backend URL:', BACKEND_URL);
        console.log('📍 Current origin:', window.location.origin);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 5000);

        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include',
          signal: controller.signal
        });

        clearTimeout(timeoutId);

        console.log('🔍 Auth response status:', response.status);

        if (!response.ok) {
          console.log('🔍 Auth check failed with status:', response.status);
          setIsAuthenticated(false);
          return;
        }

        const data = await response.json();
        console.log('🔍 Auth response data:', data);

        // Handle ALL possible response formats
        let userData = null;

        if (data.authenticated === true && data.user) {
          // New format: {authenticated: true, user: {...}}
          userData = data.user;
        } else if (data.authenticated === false) {
          // Auth failed format: {authenticated: false, ...}
          console.log('🔍 Server says not authenticated');
          setIsAuthenticated(false);
          return;
        } else if (data.email && data.access_level) {
          // Old format: direct user object
          userData = data;
        } else if (data.user_id) {
          // Another possible user object format
          userData = data;
        } else {
          // Unknown format
          console.log('🔍 Unknown auth response format:', data);
          setIsAuthenticated(false);
          return;
        }

        if (userData) {
          console.log('🔍 Setting user:', userData.email);
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }

      } catch (error) {
        console.log('🔍 Auth check error:', error);
        setIsAuthenticated(false);
      }
    };

    // Helper function to try real auth for debugging
    const tryRealAuth = async () => {
      try {
        const response = await fetch(`${BACKEND_URL}/api/auth/me`, {
          credentials: 'include'
        });
        const data = await response.json();
        console.log('🔍 Real auth debug:', data);
      } catch (error) {
        console.log('🔍 Real auth failed (expected in dev mode):', error.message);
      }
    };

    checkAuth();
  }, [navigate, user, location.state]);

  // Fetch markets and assets when authenticated
  useEffect(() => {
    if (!isAuthenticated) {
      console.log('🔍 Not authenticated, skipping data fetch');
      return;
    }

    const fetchInitialData = async () => {
      try {
        setIsLoading(prev => ({ ...prev, markets: true, assets: true }));

        // Fetch markets and assets WITHOUT credentials (public endpoints)
        const [marketsResponse, assetsResponse] = await Promise.all([
          fetch(`${BACKEND_URL}/api/markets`),
          fetch(`${BACKEND_URL}/api/assets`)
        ]);

        if (marketsResponse.ok) {
          const marketsData = await marketsResponse.json();
          setMarkets(marketsData);
          console.log('✅ Markets loaded:', marketsData.length);
        } else {
          console.log('❌ Markets fetch failed:', marketsResponse.status);
        }

        if (assetsResponse.ok) {
          const assetsData = await assetsResponse.json();
          setAssets(assetsData);
          console.log('✅ Assets loaded:', assetsData.length);
        } else {
          console.log('❌ Assets fetch failed:', assetsResponse.status);
        }

      } catch (error) {
        console.error('❌ Error fetching initial data:', error);
      } finally {
        setIsLoading(prev => ({ ...prev, markets: false, assets: false }));
      }
    };

    fetchInitialData();
  }, [isAuthenticated]);

  // Function to get assets by market
  const getAssetsByMarket = (marketId) => {
    return assets.filter(asset => asset.market_id === marketId);
  };

  // Fetch analysis for selected asset - FIXED VERSION
  const fetchAnalysis = async (assetId) => {
    if (!assetId) return;

    setIsLoading(prev => ({ ...prev, analysis: true }));
    setAnalysis(null);
    setAccessDenied(false);

    try {
      console.log(`🔍 Fetching analysis for asset ${assetId}`);

      // DEVELOPMENT MODE: Return dummy analysis
      if (DEVELOPMENT_MODE) {
        console.log('🔧 DEVELOPMENT MODE: Returning dummy analysis');
        setTimeout(() => {
          const dummyAnalysis = {
            analysis_id: "analysis_dev_123",
            asset_id: assetId,
            bias: "Bullish",
            key_levels: "Support: 150.50\nResistance: 155.75\nKey Level: 152.00",
            scenario_text: "Market shows strong bullish momentum with potential breakout above resistance levels.",
            insight_text: "Higher timeframe analysis confirms uptrend with increasing volume.",
            risk_note: "Watch for reversal at resistance levels. Stop loss recommended below 149.00.",
            confidence_level: "High",
            updated_at: new Date().toISOString(),
            created_by: "system"
          };
          setAnalysis(dummyAnalysis);
          setIsLoading(prev => ({ ...prev, analysis: false }));
        }, 500);
        return;
      }

      // FIXED: Changed endpoint to /api/analysis/{assetId}
      const response = await fetch(`${BACKEND_URL}/api/analysis/${assetId}`, {
        credentials: 'include'
      });

      console.log(`🔍 Analysis response status: ${response.status}`);

      if (response.status === 403) {
        console.log('🔍 Access denied (403) for analysis');
        setAccessDenied(true);
        return;
      }

      if (response.ok) {
        const data = await response.json();
        setAnalysis(data);
        console.log('✅ Analysis loaded successfully');
      } else if (response.status === 401) {
        // Not authenticated - don't redirect, just log
        console.log('🔍 Analysis fetch returned 401 (not authenticated)');
        setAccessDenied(true);
      } else {
        console.log(`🔍 Analysis fetch failed with status: ${response.status}`);
        setAnalysis(null);
      }
    } catch (error) {
      console.error('❌ Error fetching analysis:', error);
      setAnalysis(null);
    } finally {
      if (!DEVELOPMENT_MODE) {
        setIsLoading(prev => ({ ...prev, analysis: false }));
      }
    }
  };

  // Handle asset selection
  const handleAssetClick = (asset) => {
    console.log('🔍 Asset selected:', asset.asset_id, asset.name_en);
    setSelectedAsset(asset);
    fetchAnalysis(asset.asset_id);
  };

  // Toggle market expansion
  const toggleMarket = (marketId) => {
    setExpandedMarkets(prev => ({
      ...prev,
      [marketId]: !prev[marketId]
    }));
  };

  // Handle logout
  const handleLogout = async () => {
    console.log('🔍 Logout initiated');

    if (DEVELOPMENT_MODE) {
      console.log('🔧 DEVELOPMENT MODE: Simulating logout');
      navigate('/');
      return;
    }

    try {
      await fetch(`${BACKEND_URL}/api/auth/logout`, {
        method: 'POST',
        credentials: 'include'
      });
    } catch (error) {
      console.error('Logout error:', error);
    }

    navigate('/');
  };

  // Handle home navigation
  const handleHome = () => {
    navigate('/');
  };

  // Loading state
  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">{t('loading') || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  // Show a message if not authenticated instead of redirecting
  if (!isAuthenticated && !DEVELOPMENT_MODE) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-background p-4">
        <Card className="p-8 max-w-md w-full text-center">
          <Lock className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
          <h1 className="text-2xl font-bold text-foreground mb-2">
            {language === 'ar' ? 'جلسة غير نشطة' : 'Session Not Active'}
          </h1>
          <p className="text-muted-foreground mb-6">
            {language === 'ar'
              ? 'يرجى تسجيل الدخول للوصول إلى لوحة التحكم'
              : 'Please login to access the dashboard'
            }
          </p>
          <div className="space-y-3">
            <Button
              onClick={() => navigate('/login')}
              className="w-full"
            >
              {language === 'ar' ? 'تسجيل الدخول' : 'Sign In'}
            </Button>
            <Button
              onClick={() => navigate('/')}
              variant="outline"
              className="w-full"
            >
              {language === 'ar' ? 'العودة إلى الصفحة الرئيسية' : 'Back to Home'}
            </Button>
          </div>
          <div className="mt-6 pt-6 border-t border-border">
            <p className="text-sm text-muted-foreground">
              {language === 'ar'
                ? 'ملاحظة: المصادقة معطلة حاليًا للتطوير'
                : 'Note: Authentication is currently disabled for development'
              }
            </p>
          </div>
        </Card>
      </div>
    );
  }

  // DEVELOPMENT MODE: Show development banner
  const DevelopmentBanner = () => {
    if (!DEVELOPMENT_MODE) return null;

    return (
      <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-3 mx-4 my-2">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 rounded-full bg-yellow-500 animate-pulse"></div>
            <span className="text-sm font-medium text-yellow-800 dark:text-yellow-400">
              {language === 'ar' ? 'وضع التطوير' : 'Development Mode'}
            </span>
          </div>
          <span className="text-xs text-yellow-600 dark:text-yellow-500">
            {language === 'ar' ? 'المصادقة معطلة' : 'Authentication Disabled'}
          </span>
        </div>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-background" dir={language === 'ar' ? 'rtl' : 'ltr'}>
      {/* Development Banner */}
      <DevelopmentBanner />

      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <div className="flex items-center gap-3">
            {/* HOME BUTTON - NEW */}
            <Button
              onClick={handleHome}
              variant="ghost"
              size="sm"
              className="gap-2 text-muted-foreground hover:text-foreground"
              data-testid="home-button"
            >
              <Home className="w-4 h-4" />
              <span className="hidden sm:inline">{t('home') || 'Home'}</span>
            </Button>
            <h1 className="text-2xl font-bold text-foreground">{t('appName') || 'Trading Platform'}</h1>
          </div>
          <div className="flex items-center gap-4">
            <LanguageSwitcher />
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-sm font-medium text-primary">
                  {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                </span>
              </div>
              <span className="text-sm text-muted-foreground hidden sm:inline">
                {user?.name || 'User'} {DEVELOPMENT_MODE && '(Dev)'}
              </span>
            </div>
            <Button
              onClick={handleLogout}
              variant="ghost"
              size="sm"
              className="gap-2"
              data-testid="logout-button"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">{t('logout') || 'Logout'}</span>
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="grid grid-cols-12 gap-4 p-4 h-[calc(100vh-80px)]">
        {/* Markets Sidebar */}
        <div className={`col-span-12 lg:col-span-2 hidden lg:flex flex-col ${language === 'ar' ? 'border-l' : 'border-r'} border-border/50 bg-card/50 backdrop-blur-sm rounded-lg overflow-hidden`}>
          <div className="p-4 border-b border-border/50">
            <h2 className="text-lg font-semibold text-foreground">{t('markets') || 'Markets'}</h2>
          </div>

          <ScrollArea className="flex-1 relative z-10" data-testid="markets-navigation">
            {isLoading.markets ? (
              <div className="p-4 flex items-center justify-center">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary"></div>
              </div>
            ) : markets.length === 0 ? (
              <div className="p-4 text-center">
                <p className="text-sm text-muted-foreground">{t('noMarkets') || 'No markets available'}</p>
                <p className="text-xs text-muted-foreground mt-1">
                  {DEVELOPMENT_MODE && '(Check backend /api/markets endpoint)'}
                </p>
              </div>
            ) : (
              <div className="p-2 space-y-2">
                {markets.map((market) => {
                  const marketAssets = getAssetsByMarket(market.market_id);
                  const isExpanded = expandedMarkets[market.market_id];

                  return (
                    <div key={market.market_id}>
                      <button
                        onClick={() => toggleMarket(market.market_id)}
                        className={`w-full p-3 hover:bg-accent/50 rounded-lg transition-colors flex items-center justify-between ${language === 'ar' ? 'text-right' : 'text-left'}`}
                        data-testid={`market-${market.market_id}`}
                      >
                        <span className="font-medium text-foreground">
                          {language === 'ar' ? market.name_ar : market.name_en}
                        </span>
                        {isExpanded ? <ChevronDown className="w-4 h-4" /> : (language === 'ar' ? <ChevronLeft className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4 rotate-180" />)}
                      </button>

                      {isExpanded && (
                        <div className={`space-y-1 ${language === 'ar' ? 'mr-4' : 'ml-4'}`}>
                          {marketAssets.length === 0 ? (
                            <p className="text-xs text-muted-foreground p-2">
                              {t('noAssets') || 'No assets'}
                            </p>
                          ) : (
                            marketAssets.map((asset) => (
                              <button
                                key={asset.asset_id}
                                onClick={() => handleAssetClick(asset)}
                                className={`w-full p-2 rounded-lg transition-colors text-sm ${language === 'ar' ? 'text-right' : 'text-left'} ${
                                  selectedAsset?.asset_id === asset.asset_id
                                    ? 'bg-primary text-primary-foreground'
                                    : 'hover:bg-accent/50 text-muted-foreground'
                                }`}
                                data-testid={`asset-${asset.asset_id}`}
                              >
                                {language === 'ar' ? asset.name_ar : asset.name_en}
                              </button>
                            ))
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </div>

        {/* Main Analysis Area */}
        <div className="col-span-12 lg:col-span-7 flex flex-col bg-card rounded-lg shadow-sm border border-border/50 overflow-hidden" data-testid="analysis-display">
          <SubscriptionBanner />

          <div className="flex-1 p-6 overflow-y-auto">
            {!selectedAsset ? (
              <div className="space-y-8">
                {/* Welcome Section */}
                <div className="text-center py-4">
                  <h2 className="text-2xl font-bold text-foreground mb-2">
                    {DEVELOPMENT_MODE ? '🚀 Development Dashboard' : t('welcome') || 'Welcome to your dashboard'}
                  </h2>
                  <p className="text-muted-foreground">
                    {DEVELOPMENT_MODE
                      ? 'Authentication disabled. All features available for testing.'
                      : t('selectAssetToView') || 'Select an asset from the sidebar to view analysis'
                    }
                  </p>
                  {DEVELOPMENT_MODE && (
                    <div className="mt-4 inline-flex items-center gap-2 px-3 py-1 bg-primary/10 text-primary rounded-full text-sm">
                      <div className="w-2 h-2 rounded-full bg-primary animate-pulse"></div>
                      Development Mode Active
                    </div>
                  )}
                </div>

                {/* History of Success */}
                <HistoryOfSuccess autoRefreshInterval={60000} />

                {/* Market Overview */}
                <div className="space-y-6">
                  <div className="flex items-center gap-2 mb-4">
                    <BarChart3 className="w-5 h-5 text-primary" />
                    <h3 className="text-lg font-semibold text-foreground">
                      {language === 'ar' ? 'نظرة عامة على السوق' : 'Market Overview'}
                    </h3>
                  </div>

                  <Tabs defaultValue="line" className="w-full">
                    <TabsList className="grid w-full grid-cols-2 max-w-md mx-auto mb-4">
                      <TabsTrigger value="line" className="flex items-center gap-2">
                        <LineChartIcon className="w-4 h-4" />
                        {language === 'ar' ? 'مقارنة الأسعار' : 'Price Comparison'}
                      </TabsTrigger>
                      <TabsTrigger value="pie" className="flex items-center gap-2">
                        <PieChartIcon className="w-4 h-4" />
                        {language === 'ar' ? 'التوزيع' : 'Distribution'}
                      </TabsTrigger>
                    </TabsList>

                    <TabsContent value="line" className="mt-0">
                      <AnalysisPriceLineChart autoRefreshInterval={30000} />
                    </TabsContent>

                    <TabsContent value="pie" className="mt-0">
                      <AnalysisPriceChart autoRefreshInterval={30000} />
                    </TabsContent>
                  </Tabs>
                </div>
              </div>
            ) : accessDenied ? (
              <div className="h-full flex items-center justify-center">
                <Card className="p-8 max-w-md text-center bg-destructive/5 border-destructive/20">
                  <Lock className="w-16 h-16 mx-auto mb-4 text-destructive" />
                  <h2 className="text-xl font-bold text-foreground mb-2">
                    {language === 'ar' ? 'اشتراك مطلوب' : 'Subscription Required'}
                  </h2>
                  <p className="text-muted-foreground mb-4">
                    {language === 'ar'
                      ? 'يتطلب اشتراك نشط لعرض التحليلات.'
                      : 'An active subscription is required to view analysis.'
                    }
                  </p>
                  {subscriptionStatus && (
                    <div className="text-sm text-muted-foreground">
                      {language === 'ar' ? 'الحالة: ' : 'Status: '}
                      <span className="font-semibold">
                        {subscriptionStatus.subscription_status === 'expired'
                          ? (language === 'ar' ? 'منتهي' : 'Expired')
                          : (language === 'ar' ? 'غير مفعل' : 'None')
                        }
                      </span>
                    </div>
                  )}
                </Card>
              </div>
            ) : isLoading.analysis ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                  <p className="text-muted-foreground">
                    {t('loadingAnalysis') || 'Loading analysis...'}
                  </p>
                  {DEVELOPMENT_MODE && (
                    <p className="text-xs text-muted-foreground mt-2">
                      (Development: Simulating 500ms delay)
                    </p>
                  )}
                </div>
              </div>
            ) : !analysis ? (
              <div className="h-full flex items-center justify-center">
                <div className="text-center">
                  <h2 className="text-xl font-bold text-foreground mb-2">
                    {language === 'ar' ? selectedAsset.name_ar : selectedAsset.name_en}
                  </h2>
                  <p className="text-muted-foreground">
                    {t('noAnalysis') || 'No analysis available for this asset'}
                  </p>
                  {DEVELOPMENT_MODE && (
                    <p className="text-xs text-muted-foreground mt-2">
                      (Development: Should show dummy analysis)
                    </p>
                  )}
                </div>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Asset Header with Development Badge */}
                <div className="flex items-start justify-between">
                  <div>
                    <h1 className="text-4xl font-bold text-foreground mb-2">
                      {language === 'ar' ? selectedAsset.name_ar : selectedAsset.name_en}
                    </h1>
                    <p className="text-lg text-muted-foreground">
                      {language === 'ar' ? selectedAsset.name_en : selectedAsset.name_ar}
                    </p>
                  </div>
                  {DEVELOPMENT_MODE && (
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-primary/10 text-primary rounded-full text-xs">
                      Dev Data
                    </span>
                  )}
                </div>

                {/* Bias and Confidence */}
                <Card className="p-6 bg-gradient-to-br from-primary/10 to-secondary/10 border-primary/20">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-muted-foreground mb-1">
                        {t('direction') || 'Direction'}
                      </p>
                      <p className="text-3xl font-bold text-primary">{analysis.bias}</p>
                    </div>
                    <div className={language === 'ar' ? 'text-left' : 'text-right'}>
                      <p className="text-sm text-muted-foreground mb-1">
                        {t('confidence') || 'Confidence'}
                      </p>
                      <p className="text-xl font-semibold text-foreground">
                        {analysis.confidence_level}
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Key Levels */}
                <div>
                  <h2 className="text-2xl font-semibold text-foreground mb-3">
                    {t('keyLevelsTitle') || 'Key Levels'}
                  </h2>
                  <Card className="p-6">
                    <p className="text-lg text-foreground whitespace-pre-wrap leading-relaxed">
                      {analysis.key_levels}
                    </p>
                  </Card>
                </div>

                {/* Scenario */}
                <div>
                  <h2 className="text-2xl font-semibold text-foreground mb-3">
                    {t('scenarioTitle') || 'Market Scenario'}
                  </h2>
                  <Card className="p-6">
                    <p className="text-base text-foreground whitespace-pre-wrap leading-relaxed">
                      {analysis.scenario_text}
                    </p>
                  </Card>
                </div>

                {/* Updated Time */}
                <div className={`text-xs text-muted-foreground ${language === 'ar' ? 'text-left' : 'text-right'}`}>
                  {t('lastUpdated') || 'Last updated'}:{' '}
                  {new Date(analysis.updated_at).toLocaleString(language === 'ar' ? 'ar-SA' : 'en-US')}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Insights Sidebar */}
        <div className={`col-span-3 hidden lg:flex flex-col ${language === 'ar' ? 'border-r' : 'border-l'} border-border/50 bg-card/50 backdrop-blur-sm rounded-lg overflow-hidden`}>
          <div className="p-4 border-b border-border/50">
            <h2 className="text-lg font-semibold text-foreground">
              {t('additionalInsights') || 'Additional Insights'}
            </h2>
          </div>

          <ScrollArea className="flex-1 relative z-10" data-testid="insights-panel">
            {analysis ? (
              <div className="p-4 space-y-4">
                {analysis.insight_text && (
                  <Card className="p-4">
                    <h3 className="font-semibold text-foreground mb-2">
                      {t('higherTimeframe') || 'Higher Timeframe Analysis'}
                    </h3>
                    <p className="text-sm text-muted-foreground whitespace-pre-wrap leading-relaxed">
                      {analysis.insight_text}
                    </p>
                  </Card>
                )}

                {analysis.risk_note && (
                  <Card className="p-4 bg-destructive/10 border-destructive/20">
                    <h3 className="font-semibold text-destructive mb-2">
                      {t('riskWarning') || 'Risk Warning'}
                    </h3>
                    <p className="text-sm text-foreground whitespace-pre-wrap leading-relaxed">
                      {analysis.risk_note}
                    </p>
                  </Card>
                )}

                {!analysis.insight_text && !analysis.risk_note && (
                  <p className="text-sm text-muted-foreground text-center py-8">{t('noInsights') || 'No additional insights available'}</p>
                )}
              </div>
            ) : (
              <div className="text-center py-8">
                <p className="text-sm text-muted-foreground">
                  {t('selectAssetForInsights') || 'Select an asset to see insights'}
                </p>
              </div>
            )}
          </ScrollArea>
        </div>
      </div>
    </div>
  );
}
